// SP6 Task 4 — runs the SAME shared validator the publish wall uses
// (`validateRemittanceConfig`) live on every draft change, and buckets the
// resulting RemittanceConfigIssue[] by section so each Remittance tab
// section (Basics, Allocation, BalancePolicy, TransactionRules,
// TransactionFields, per-form panels) can surface just its own issues
// without re-deriving the mapping.
import { useMemo } from 'react';
import { validateRemittanceConfig, type RemittanceConfigIssue } from '@capture/shared';
import { useFormConfigStore } from '../state/store';

export type RemittanceIssueBucket = 'basics' | 'allocation' | 'policy' | 'rules' | 'fields' | 'forms' | 'other';

function emptyBuckets(): Record<RemittanceIssueBucket, RemittanceConfigIssue[]> {
  return { basics: [], allocation: [], policy: [], rules: [], fields: [], forms: [], other: [] };
}

// Enumerated from packages/shared/src/remittance/validate-config.ts — the
// only codes NOT covered by the formId check or a `remittance_policy_` /
// `remittance_txnrule_` / `remittance_txnfield_` prefix.
const ALLOCATION_CODES = new Set([
  'allocation_target_ids_invalid',
  'allocation_ratio_unknown_target',
  'allocation_ratio_sum_invalid',
  'allocation_priority_order_invalid',
]);

const BASICS_CODES = new Set([
  'missing_remittance_job_config',
  'dual_control_not_boolean',
]);

/**
 * Pure bucketer, exported for direct testing. Priority: a per-form issue
 * (carries `formId`) always routes to 'forms', even if its code also
 * matches a section prefix — the form panel is where the operator fixes it.
 * `remittance_policy_target_unknown` intentionally stays in 'policy' here;
 * the Allocation section separately surfaces policy issues that reference
 * unknown targets — that's the section's own concern, not duplicated here.
 */
export function bucketRemittanceIssues(
  issues: RemittanceConfigIssue[],
): Record<RemittanceIssueBucket, RemittanceConfigIssue[]> {
  const buckets = emptyBuckets();
  for (const issue of issues) {
    if (issue.formId) {
      buckets.forms.push(issue);
    } else if (issue.code.startsWith('remittance_policy_')) {
      buckets.policy.push(issue);
    } else if (issue.code.startsWith('remittance_txnrule_')) {
      buckets.rules.push(issue);
    } else if (issue.code.startsWith('remittance_txnfield_')) {
      buckets.fields.push(issue);
    } else if (ALLOCATION_CODES.has(issue.code)) {
      buckets.allocation.push(issue);
    } else if (BASICS_CODES.has(issue.code)) {
      buckets.basics.push(issue);
    } else {
      buckets.other.push(issue);
    }
  }
  return buckets;
}

/** Live validator issues for the current draft, memoized on content
 *  identity and bucketed by section. `contentV3` null (no draft loaded)
 *  returns all-empty buckets. */
export function useRemittanceIssues(): Record<RemittanceIssueBucket, RemittanceConfigIssue[]> {
  const contentV3 = useFormConfigStore((s) => s.contentV3);
  return useMemo(() => {
    if (!contentV3) return emptyBuckets();
    return bucketRemittanceIssues(validateRemittanceConfig(contentV3, 'remittance'));
  }, [contentV3]);
}
